// Product Sequences GSAP Animation
// Same approach as ville-hero - clean GSAP image sequence on scroll

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

// Make GSAP globally available
if (!window.gsap) {
  window.gsap = gsap;
}
if (!window.ScrollTrigger) {
  window.ScrollTrigger = ScrollTrigger;
}

// Optimize GSAP for better performance
gsap.config({
  force3D: true,
  nullTargetWarn: false
});

gsap.ticker.lagSmoothing(0);

function productSequences() {
  console.log(performance.now());
  
  // Frame counts for each product sequence type
  const frameCounts = {
    'industrivarmepumpe': 26,
    'isolasjon': 26,
    'lampe': 16,
    'laptop': 16,
    'solceller': 16,
    'vifte': 25
  };

  // Determine which responsive folder to use based on screen size
  function getImagePath(productSequenceValue) {
    const width = window.innerWidth;
    
    if (width < 1024) {
      return `product-sequences/${productSequenceValue}-mobile`;
    } else {
      return `product-sequences/${productSequenceValue}`;
    }
  }

  // Function to get image source for a specific frame with S3 optimization
  function getImageSrc(productSequenceValue, frameIndex) {
    const path = getImagePath(productSequenceValue);
    const paddedNumber = frameIndex.toString().padStart(2, '0');
    // Add cache-busting parameter only when cache is disabled to prevent cancellation
    const cacheParam = window.performance && window.performance.navigation && window.performance.navigation.type === 1 ? `?v=${Date.now()}` : '';
    return `https://acron.ams3.digitaloceanspaces.com/ville/${path}/${productSequenceValue}${paddedNumber}.webp${cacheParam}`;
  }

  // Find all product sequence images
  const images = document.querySelectorAll('img[id^="product-seq-"]');

  if (images.length === 0) {
    console.log('ProductSequences: No product sequence images found');
    return;
  }


  // Initialize all product sequences immediately
  function initializeAllProductSequences() {
    // Preload all sequences immediately, but control animations separately
    images.forEach(img => {
      const productSequenceValue = img.getAttribute('data-product-sequence');
      
      if (productSequenceValue && !img._sequenceInitialized) {
        console.log(`Product ${productSequenceValue}`, performance.now());
        img._sequenceInitialized = true;
        preloadProductSequence(img, productSequenceValue);
      }
    });
  }

  // Preload images for a product sequence without starting animation
  function preloadProductSequence(img, productSequenceValue) {
    const frameCount = frameCounts[productSequenceValue] || 30;
    const section = img.closest('[animate-parent]');
    
    if (!section) {
      return;
    }
    
    // Preload images and set up intersection observer for animation timing
    preloadSequenceImages(img, productSequenceValue, frameCount, section);
  }

  // Initialize a single product sequence (legacy function for compatibility)
  function initializeProductSequence(img, productSequenceValue) {
    preloadProductSequence(img, productSequenceValue);
  }

  // Preload images for a specific product sequence with better S3 optimization
  function preloadSequenceImages(img, productSequenceValue, frameCount, section) {
    let loadedCount = 0;
    const imageCache = {};
    
    // Preload all frames for this sequence with optimized loading
    for (let i = 0; i < frameCount; i++) {
      const preloadImg = new Image();
      preloadImg.src = getImageSrc(productSequenceValue, i);
      imageCache[i] = preloadImg;
      
      preloadImg.onload = () => {
        loadedCount++;
        const progress = Math.round((loadedCount / frameCount) * 100);
        // console.log(`ProductSequences: Loaded ${productSequenceValue} frame ${i+1}/${frameCount} (${progress}%)`);
        
        if (loadedCount === frameCount) {
          // Store cache globally for reuse
          img._imageCache = imageCache;
          setupIntersectionObserverForAnimation(img, section, productSequenceValue, frameCount);
        }
      };
      
      preloadImg.onerror = () => {
        loadedCount++;
        console.warn(`ProductSequences: Failed to load ${productSequenceValue} frame ${i}`);
        
        if (loadedCount === frameCount) {
          img._imageCache = imageCache;
          setupIntersectionObserverForAnimation(img, section, productSequenceValue, frameCount);
        }
      };
    }
  }

  // Set up intersection observer to control when animations start
  function setupIntersectionObserverForAnimation(img, section, productSequenceValue, frameCount) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !img._animationStarted) {
          img._animationStarted = true;
          setupScrollAnimation(img, section, productSequenceValue, frameCount);
          observer.unobserve(section);
        }
      });
    }, {
      rootMargin: '100px 0px', // Start animation when 100px away from viewport
      threshold: 0.1
    });

    observer.observe(section);
  }

  // Set up scroll animation
  function setupScrollAnimation(img, section, productSequenceValue, frameCount) {
    if (!window.gsap || !window.ScrollTrigger) {
      console.error('GSAP/ScrollTrigger not loaded.');
      return;
    }
    
    gsap.registerPlugin(ScrollTrigger);

    const current = { frame: 0 };
    
    // Determine animation behavior based on product sequence type
    const isContinuousLoop = productSequenceValue === 'vifte' || productSequenceValue === 'industrivarmepumpe';

    if (isContinuousLoop) {
      // Continuous loop animation for spinning fans
      setupContinuousLoopAnimation(img, section, productSequenceValue, frameCount, current);
    } else {
      // Pinned animation for other sections
      setupPinnedAnimation(img, section, productSequenceValue, frameCount, current);
    }
  }

  // Continuous loop animation for spinning fans
  function setupContinuousLoopAnimation(img, section, productSequenceValue, frameCount, current) {

    // Create a canvas to cycle through preloaded images without network requests
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.style.cssText = img.style.cssText;
    canvas.className = img.className;
    canvas.id = img.id + '-canvas';
    
    // Replace the img element with canvas
    img.parentNode.replaceChild(canvas, img);
    
    // Use pre-cached images if available
    const imageCache = img._imageCache || {};
    let loadedImages = 0;
    
    // Load any missing images
    for (let i = 0; i < frameCount; i++) {
      if (!imageCache[i]) {
        const cachedImg = new Image();
        cachedImg.src = getImageSrc(productSequenceValue, i);
        cachedImg.onload = () => {
          loadedImages++;
          if (loadedImages === frameCount) {
            startLoopAnimation();
          }
        };
        imageCache[i] = cachedImg;
      } else {
        loadedImages++;
        if (loadedImages === frameCount) {
          startLoopAnimation();
        }
      }
    }
    
    let loopAnimation; // Declare outside so ScrollTrigger can access it

    function startLoopAnimation() {
      // Create a continuous loop animation
      loopAnimation = gsap.to(current, {
        frame: frameCount - 1,
        ease: 'none',
        duration: 2, // 2 seconds per loop
        repeat: -1, // Infinite repeat
        onUpdate: () => {
          const frame = Math.round(current.frame);
          if (frame >= 0 && frame < frameCount && imageCache[frame]) {
            // Draw the cached image to canvas instead of changing src
            const cachedImg = imageCache[frame];
            if (cachedImg.complete) {
              canvas.width = cachedImg.naturalWidth;
              canvas.height = cachedImg.naturalHeight;
              ctx.drawImage(cachedImg, 0, 0);
            }
          }
        },
      });

      // Pause/resume loop based on visibility
      ScrollTrigger.create({
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => {
          if (loopAnimation) loopAnimation.play();
        },
        onLeave: () => {
          if (loopAnimation) loopAnimation.pause();
        },
        onEnterBack: () => {
          if (loopAnimation) loopAnimation.play();
        },
        onLeaveBack: () => {
          if (loopAnimation) loopAnimation.pause();
        },
      });
    }
  }

  // Pinned animation for other sections
  function setupPinnedAnimation(img, section, productSequenceValue, frameCount, current) {
    // Create canvas to prevent network requests during animation
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.style.cssText = img.style.cssText;
    canvas.className = img.className;
    canvas.id = img.id + '-canvas';
    
    // Replace the img element with canvas
    img.parentNode.replaceChild(canvas, img);
    
    // Use pre-cached images if available
    const imageCache = img._imageCache || {};
    
    function startPinnedAnimation() {
      gsap.to(current, {
        frame: frameCount - 1,
        ease: 'none',
        snap: 'frame',
        scrollTrigger: {
          trigger: section,
          start: 'top-=300px top',
          end: '+=300px',
          scrub: 0.05,
          invalidateOnRefresh: true,
          onUpdate: () => {
            const frame = Math.round(current.frame);
            if (frame >= 0 && frame < frameCount && imageCache[frame]) {
              // Draw cached image to canvas instead of changing src
              if (frame !== canvas._lastFrame) {
                canvas._lastFrame = frame;
                const cachedImg = imageCache[frame];
                if (cachedImg.complete) {
                  canvas.width = cachedImg.naturalWidth;
                  canvas.height = cachedImg.naturalHeight;
                  ctx.drawImage(cachedImg, 0, 0);
                }
              }
            }
          },
          onRefresh: () => {
            const frame = Math.round(current.frame);
            if (frame >= 0 && frame < frameCount && imageCache[frame]) {
              canvas._lastFrame = frame;
              const cachedImg = imageCache[frame];
              if (cachedImg.complete) {
                canvas.width = cachedImg.naturalWidth;
                canvas.height = cachedImg.naturalHeight;
                ctx.drawImage(cachedImg, 0, 0);
              }
            }
          },
        },
      });
    }
    
    // Start animation immediately if cache is ready, otherwise wait
    if (Object.keys(imageCache).length === frameCount) {
      startPinnedAnimation();
    } else {
      // Fallback: load images if cache is not ready
      let loadedImages = 0;
      for (let i = 0; i < frameCount; i++) {
        if (!imageCache[i]) {
          const cachedImg = new Image();
          cachedImg.src = getImageSrc(productSequenceValue, i);
          imageCache[i] = cachedImg;
          
          cachedImg.onload = () => {
            loadedImages++;
            if (loadedImages === frameCount) {
              startPinnedAnimation();
            }
          };
        }
      }
    }
  }

  // Handle responsive path changes
  function handleResize() {
    images.forEach(img => {
      const productSequenceValue = img.getAttribute('data-product-sequence');
      if (productSequenceValue) {
        // Update current frame with new path
        const currentFrame = Math.round(gsap.getProperty(gsap.getById('productSequenceAnimation'), 'frame') || 0);
        img.src = getImageSrc(productSequenceValue, currentFrame);
      }
    });
    
    // Refresh ScrollTrigger
    if (window.ScrollTrigger) {
      ScrollTrigger.refresh();
    }
  }

  // Resize handler
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      handleResize();
    }, 100);
  });
  
  // Wait for villeBanner to be ready before initializing
  function waitForVilleBanner() {
    
    window.addEventListener('villeBannerReady', () => {
      setTimeout(() => {
        initializeAllProductSequences();
      }, 500);
    }, { once: true });
  }

  // Start waiting for villeBanner
  waitForVilleBanner();
}

export default productSequences;
